const GIFT_DEBOUNCE_MS = 220;
let giftDebounceTimer = null;
let giftSyncInFlight = false;
let giftSyncPending = false;
let giftPendingOnDone = null;

const EXCLUSIVE_GIFT_OPT_OUT_KEY = 'exclusive_gift_opt_out';
const GIFT_PROPERTY_PREFIX = '_G_';
const GIFT_PDP_MARKER = '_G_FromPdp';

function isLineFromPdp(lineProperties) {
  const props = lineProperties || {};
  if (props[GIFT_PDP_MARKER] === '1') {
    return true;
  }
  return Object.keys(props).some(function (k) {
    return k.indexOf(GIFT_PROPERTY_PREFIX) === 0;
  });
}

function isGiftSelectedOnLine(lineProperties, giftVariantId) {
  if (giftVariantId == null || giftVariantId === '') {
    return false;
  }
  const props = lineProperties || {};
  return props[GIFT_PROPERTY_PREFIX + String(giftVariantId)] != null;
}

function lineMatchesGiftRule(line, rule) {
  const mainIds = (rule.products || '').split(',').filter(Boolean);
  if (rule.type === 'handle') {
    return mainIds.includes(line.handle || '');
  }
  if (rule.type === 'variant') {
    return mainIds.includes(String(line.variant_id));
  }
  return false;
}

/**
 * 级联关闭规则的赠品变体集合：这些规则的 giftId 对应的「自动赠送行」（带 _gift_from 属性）
 * 不得作为其他买赠规则的父商品（是否送子赠品开关，默认关闭）。手动加购的行不受影响。
 */
function cascadeBlockedGiftIds() {
  const ids = new Set();
  const raw = typeof gift_data !== 'undefined' ? gift_data : null;
  if (!Array.isArray(raw)) {
    return ids;
  }
  for (let i = 0; i < raw.length; i++) {
    const g = raw[i];
    if (g && g.giftId && g.cascade !== true) {
      ids.add(String(g.giftId));
    }
  }
  return ids;
}

/**
 * 「每单只送 1 件」规则的赠品变体集合：订单级赠品（分组展示在顶部独立区、不挂主商品），
 * 其自动赠送行同样不触发下游规则——否则下游赠品按 _gift_from 挂到一个无主行上，
 * 分组判孤儿隐藏，造成不可见残留。手动加购的行不受影响。
 */
function onePerOrderGiftIds() {
  const ids = new Set();
  const raw = typeof gift_data !== 'undefined' ? gift_data : null;
  if (!Array.isArray(raw)) {
    return ids;
  }
  for (let i = 0; i < raw.length; i++) {
    const g = raw[i];
    if (g && g.giftId && g.onePerOrder === true) {
      ids.add(String(g.giftId));
    }
  }
  return ids;
}

/** 赠品行判定：带 _gift_from 隐藏属性的行 = 由买赠规则自动赠送 */
function isAutoGiftedLine(line) {
  return !!(line.properties && line.properties['_gift_from']);
}

/**
 * ShokzSquad 认领行判定：会员升级礼（_ShokzSquad_gift_tier_）/ 转盘抽奖礼（_ShokzSquad_lottery_prize_）
 * 认领的行是系统赠送，不参与买赠规则——不作父行、不触发买赠配赠。否则认领的免费主商品
 * 会叠加一条买赠赠品行，而认领行在渲染层是无主行不能当父行，赠品行判孤儿隐藏，
 * 造成「购物车计数比可见行数多 1」的隐藏残留。正常购买的行不带认领属性，不受影响。
 */
function isClaimedGiftLine(line) {
  const props = line.properties || {};
  return !!(
    (props['_ShokzSquad_gift_tier_'] && props['_ShokzSquad_gift_tier_'] !== '') ||
    (props['_ShokzSquad_lottery_prize_'] && props['_ShokzSquad_lottery_prize_'] !== '')
  );
}

function countEligibleMainQtyForGiftRule(items, rule, giftVariantId) {
  let productNum = 0;
  const cartItems = items || [];
  const giftSid = String(giftVariantId);
  const blocked = cascadeBlockedGiftIds();
  const onePerBlocked = onePerOrderGiftIds();
  for (let i = 0; i < cartItems.length; i++) {
    const line = cartItems[i];
    if (!lineMatchesGiftRule(line, rule)) {
      continue;
    }
    // 赠品变体自身的行不得算作本规则的父商品（买X送X另一变体等自指配置会把赠品行
    // 也计入主商品数量 → 目标逐轮膨胀 +1，无限加赠死循环）
    if (giftSid !== 'undefined' && giftSid !== '' && String(line.variant_id) === giftSid) {
      continue;
    }
    // ShokzSquad 认领行（会员礼/抽奖礼）是系统赠送：不触发买赠配赠
    if (isClaimedGiftLine(line)) {
      continue;
    }
    // 「是否送子赠品」开关：级联关闭规则的自动赠送行不触发下游规则（手动加购不受影响）
    if (blocked.has(String(line.variant_id)) && isAutoGiftedLine(line)) {
      continue;
    }
    // 「每单只送 1 件」规则的自动赠送行是订单级赠品：同样不触发下游规则
    if (onePerBlocked.has(String(line.variant_id)) && isAutoGiftedLine(line)) {
      continue;
    }
    const props = line.properties || {};
    if (isLineFromPdp(props)) {
      if (isGiftSelectedOnLine(props, giftVariantId)) {
        productNum += line.quantity || 0;
      }
    } else {
      productNum += line.quantity || 0;
    }
  }
  return productNum;
}

/**
 * 逐行枚举某赠品规则的可计父行（与 countEligibleMainQtyForGiftRule 同口径，但保留行粒度）：
 * 供赠品同步按父行拆分赠品行（_gift_from 属性绑定父行 line key）时使用。
 * 赠品变体自身的行不作为父行（防自指配置无限加赠，同上）；
 * ShokzSquad 认领行（会员礼/抽奖礼，isClaimedGiftLine）是系统赠送，不作父行不触发配赠；
 * 级联关闭规则的自动赠送行、「每单只送 1 件」规则的自动赠送行也不作为父行（手动加购不受影响）。
 * @returns {Array<{line: object, qty: number}>} 按购物车行序的父行列表
 */
function eligibleParentLinesForGiftRule(items, rule, giftVariantId) {
  const result = [];
  const cartItems = items || [];
  const giftSid = String(giftVariantId);
  const blocked = cascadeBlockedGiftIds();
  const onePerBlocked = onePerOrderGiftIds();
  for (let i = 0; i < cartItems.length; i++) {
    const line = cartItems[i];
    if (!lineMatchesGiftRule(line, rule)) {
      continue;
    }
    if (giftSid !== 'undefined' && giftSid !== '' && String(line.variant_id) === giftSid) {
      continue;
    }
    if (isClaimedGiftLine(line)) {
      continue;
    }
    if (blocked.has(String(line.variant_id)) && isAutoGiftedLine(line)) {
      continue;
    }
    if (onePerBlocked.has(String(line.variant_id)) && isAutoGiftedLine(line)) {
      continue;
    }
    const props = line.properties || {};
    if (isLineFromPdp(props) && !isGiftSelectedOnLine(props, giftVariantId)) {
      continue;
    }
    result.push({ line: line, qty: line.quantity || 0 });
  }
  return result;
}

function isExclusiveGiftOptedOut(variantId) {
  const raw = localStorage.getItem(EXCLUSIVE_GIFT_OPT_OUT_KEY);
  if (!raw) {
    console.log('[DEBUG isExclusiveGiftOptedOut]', variantId, '→ no key found');
    return false;
  }
  const result = raw.indexOf(String(variantId)) !== -1;
  console.log('[DEBUG isExclusiveGiftOptedOut]', variantId, 'key=', raw, '→', result);
  return result;
}

function addExclusiveGiftOptOut(variantId) {
  if (variantId == null || variantId === '') return;
  const sid = String(variantId);
  const raw = localStorage.getItem(EXCLUSIVE_GIFT_OPT_OUT_KEY) || '';
  if (!raw) {
    localStorage.setItem(EXCLUSIVE_GIFT_OPT_OUT_KEY, sid);
  } else if (raw.indexOf(sid) === -1) {
    localStorage.setItem(EXCLUSIVE_GIFT_OPT_OUT_KEY, raw + ',' + sid);
  }
  console.log('[DEBUG addExclusiveGiftOptOut]', sid, '→', localStorage.getItem(EXCLUSIVE_GIFT_OPT_OUT_KEY));
}

function flushExclusiveOnDone() {
  const onDone = giftPendingOnDone;
  giftPendingOnDone = null;
  if (typeof onDone === 'function') {
    onDone();
  }
}

function getExclusiveGiftConfig() {
  const cfg = typeof window !== 'undefined' ? window.__exclusiveVariantGifts : null;
  if (!cfg) return null;
  // 新扁平格式：groups 在顶层
  if (cfg.groups) return cfg;
  // 旧格式兼容：groups 在 .opendots2 子键下
  return cfg.opendots2 || null;
}

function getExclusivePropertyKey() {
  return getExclusiveGiftConfig()?.propertyKey || '_ExclusiveGift';
}

function buildMainVariantToGroupMap(groups) {
  const map = new Map();
  if (!groups) {
    return map;
  }
  for (let i = 0; i < groups.length; i++) {
    const group = groups[i];
    const ids = group.mainVariantIds || [];
    for (let j = 0; j < ids.length; j++) {
      map.set(String(ids[j]), group);
    }
  }
  return map;
}

function collectAllGiftVariantIds(groups) {
  const ids = new Set();
  if (!groups) {
    return ids;
  }
  for (let i = 0; i < groups.length; i++) {
    const group = groups[i];
    if (group.gift_1_VariantId) {
      ids.add(String(group.gift_1_VariantId));
    }
    if (group.gift_2_VariantId) {
      ids.add(String(group.gift_2_VariantId));
    }
  }
  return ids;
}

function qtyForVariantInCart(items, variantId) {
  const sid = String(variantId);
  let qty = 0;
  for (let i = 0; i < items.length; i++) {
    if (String(items[i].variant_id) === sid) {
      qty += items[i].quantity;
    }
  }
  return qty;
}

function isGiftVariantOptedOut(variantId) {
  const oldRaw = localStorage.getItem('product_gifts');
  if (oldRaw && oldRaw.indexOf(String(variantId)) !== -1) {
    console.log('[DEBUG isGiftVariantOptedOut]', variantId, 'found in product_gifts:', oldRaw);
    return true;
  }
  const result = isExclusiveGiftOptedOut(variantId);
  console.log('[DEBUG isGiftVariantOptedOut]', variantId, 'exclusive check →', result);
  return result;
}

function applyProductGiftsOptOut(targets, items) {
  const result = new Map(targets);
  for (const [giftVariantId, targetQty] of targets) {
    if (!isGiftVariantOptedOut(giftVariantId)) {
      continue;
    }
    const currentQty = qtyForVariantInCart(items, giftVariantId);
    if (targetQty > currentQty) {
      console.log('[DEBUG applyProductGiftsOptOut] CAPPING', giftVariantId, 'target=', targetQty, '→', currentQty);
      result.set(giftVariantId, currentQty);
    } else {
      console.log('[DEBUG applyProductGiftsOptOut] SKIP', giftVariantId, 'target=', targetQty, 'current=', currentQty, '(target ≤ current)');
    }
  }
  return result;
}

function computeTargetGiftQuantities(cart, config) {
  const groups = config.groups || [];
  const variantToGroup = buildMainVariantToGroupMap(groups);
  const targets = new Map();
  const allGiftIds = collectAllGiftVariantIds(groups);

  for (const giftId of allGiftIds) {
    targets.set(giftId, 0);
  }

  const items = cart.items || [];
  for (let i = 0; i < items.length; i++) {
    const line = items[i];
    const group = variantToGroup.get(String(line.variant_id));
    if (!group) continue;

    const qty = line.quantity || 0;
    const props = line.properties || {};
    const isPdpSourced = isLineFromPdp(props);

    if (isPdpSourced) {
      // PDP 来源的行：只加用户勾选的赠品
      if (group.gift_1_VariantId && isGiftSelectedOnLine(props, group.gift_1_VariantId)) {
        const id = String(group.gift_1_VariantId);
        targets.set(id, (targets.get(id) || 0) + qty);
      }
      if (group.gift_2_VariantId && isGiftSelectedOnLine(props, group.gift_2_VariantId)) {
        const id = String(group.gift_2_VariantId);
        targets.set(id, (targets.get(id) || 0) + qty);
      }
      // PDP 来源但无勾选：targets 保持初始化时的 0
    } else {
      // 非 PDP 来源的行：默认加所有赠品
      if (group.gift_1_VariantId) {
        const id = String(group.gift_1_VariantId);
        targets.set(id, (targets.get(id) || 0) + qty);
      }
      if (group.gift_2_VariantId) {
        const id = String(group.gift_2_VariantId);
        targets.set(id, (targets.get(id) || 0) + qty);
      }
    }
  }

  return applyProductGiftsOptOut(targets, items);
}

function buildGiftUpdates(targets, items, groups) {
  const allGiftIds = collectAllGiftVariantIds(groups);
  const updates = {};
  let hasChanges = false;

  for (const giftId of allGiftIds) {
    const target = targets.get(String(giftId)) || 0;
    const current = qtyForVariantInCart(items, giftId);
    if (target !== current) {
      updates[String(giftId)] = target;
      hasChanges = true;
    }
  }

  return hasChanges ? updates : null;
}

function runSyncExclusiveGifts() {
  const config = getExclusiveGiftConfig();
  if (!config?.groups?.length) {
    flushExclusiveOnDone();
    return Promise.resolve(false);
  }
  if (giftSyncInFlight) {
    giftSyncPending = true;
    return Promise.resolve(false);
  }

  if ((window.__exclusiveGiftRetryCount || 0) >= 5) {
    window.__exclusiveGiftRetryCount = 0;
    if (giftSyncPending) {
      giftSyncPending = false;
    }
    flushExclusiveOnDone();
    console.warn('exclusive gift sync: max retries reached, stopping');
    return Promise.resolve(false);
  }

  giftSyncInFlight = true;
  let actuallyUpdated = false;

  return Promise.all([
    fetch('/cart.js').then(function (response) { return response.json(); }),
    getExclusiveGiftOutOfStockIds(config)
  ])
    .then(function (results) {
      const cart = results[0];
      const outOfStockIds = results[1];
      const items = cart.items || [];
      const targets = computeTargetGiftQuantities(cart, config);

      // 售罄 pair 的赠品目标清零
      if (outOfStockIds.size > 0) {
        for (const [variantId, qty] of targets) {
          if (outOfStockIds.has(variantId)) {
            targets.set(variantId, 0);
          }
        }
      }

      const updates = buildGiftUpdates(targets, items, config.groups);
      if (!updates) {
        return null;
      }
      return fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ updates }),
      }).then(function (response) { return response.json(); });
    })
    .then(function (updated) {
      if (updated) {
        actuallyUpdated = true;
        typeof publish === 'function' && publish(PUB_SUB_EVENTS.cartUpdate, { source: 'exclusive-gift' });
      }
      return Boolean(updated);
    })
    .catch((error) => {
      console.error('exclusive gift sync:', error);
      return false;
    })
    .finally(() => {
      if (actuallyUpdated) {
        window.__exclusiveGiftRetryCount = (window.__exclusiveGiftRetryCount || 0) + 1;
      }
      giftSyncInFlight = false;
      if (giftSyncPending) {
        giftSyncPending = false;
        runSyncExclusiveGifts();
        return;
      }
      flushExclusiveOnDone();
    });
}

function fetchVariantAvailability(variantId, productHandle) {
  const sid = String(variantId);
  if (!productHandle) {
    return Promise.resolve(true);
  }
  return fetch('/products/' + productHandle + '.js')
    .then(function (r) { return r.json(); })
    .then(function (product) {
      const variant = (product.variants || []).find(function (v) {
        return String(v.id) === sid;
      });
      return variant ? Boolean(variant.available) : true;
    })
    .catch(function () {
      return true;
    });
}

/**
 * 检查单个 group 的赠品库存。
 * pair 配置时任一售罄则两个都阻止；否则逐赠品检查。
 */
function checkGroupGiftStock(group) {
  const outOfStock = [];
  const hasPair =
    group.gift_1_VariantId &&
    group.gift_2_VariantId &&
    group.gift_1_ProductHandle &&
    group.gift_2_ProductHandle;

  if (hasPair) {
    return Promise.all([
      fetchVariantAvailability(group.gift_1_VariantId, group.gift_1_ProductHandle),
      fetchVariantAvailability(group.gift_2_VariantId, group.gift_2_ProductHandle),
    ])
      .then(function (results) {
        if (!results[0] || !results[1]) {
          outOfStock.push(String(group.gift_1_VariantId), String(group.gift_2_VariantId));
        }
        return outOfStock;
      })
      .catch(function () {
        return outOfStock;
      });
  }

  const checks = [];
  if (group.gift_1_VariantId) {
    checks.push(
      fetchVariantAvailability(group.gift_1_VariantId, group.gift_1_ProductHandle).then(function (available) {
        if (!available) {
          outOfStock.push(String(group.gift_1_VariantId));
        }
      })
    );
  }
  if (group.gift_2_VariantId) {
    checks.push(
      fetchVariantAvailability(group.gift_2_VariantId, group.gift_2_ProductHandle).then(function (available) {
        if (!available) {
          outOfStock.push(String(group.gift_2_VariantId));
        }
      })
    );
  }
  return Promise.all(checks)
    .then(function () {
      return outOfStock;
    })
    .catch(function () {
      return outOfStock;
    });
}

/**
 * 遍历所有 group，并行检查赠品库存。
 * 返回所有需要阻止加购的 variant ID Set。
 */
function getExclusiveGiftOutOfStockIds(config) {
  const groups = (config && config.groups) || [];
  if (!groups.length) {
    return Promise.resolve(new Set());
  }

  return Promise.all(groups.map(function (group) {
    return checkGroupGiftStock(group);
  })).then(function (results) {
    const outOfStock = new Set();
    for (let i = 0; i < results.length; i++) {
      const ids = results[i];
      for (let j = 0; j < ids.length; j++) {
        outOfStock.add(ids[j]);
      }
    }
    return outOfStock;
  });
}

/**
 * 检查 gift_data 规则中赠品的库存；Liquid available:false 直接跳过 API。
 */
function getGiftDataOutOfStockIds(giftData) {
  const rules = giftData || [];
  if (!rules.length) {
    return Promise.resolve(new Set());
  }

  const outOfStock = new Set();
  const checks = [];

  for (let i = 0; i < rules.length; i++) {
    const rule = rules[i];
    const giftId = rule.giftId;
    if (giftId == null || giftId === '') {
      continue;
    }
    if (rule.available === false) {
      outOfStock.add(String(giftId));
      continue;
    }
    checks.push(
      fetchVariantAvailability(giftId, rule.giftProductHandle || null).then(function (available) {
        if (!available) {
          outOfStock.add(String(giftId));
        }
      })
    );
  }

  return Promise.all(checks)
    .then(function () {
      return outOfStock;
    })
    .catch(function () {
      return outOfStock;
    });
}

function scheduleSyncExclusiveGifts(onDone) {
  if (typeof onDone === 'function') {
    giftPendingOnDone = onDone;
  }
  if (giftDebounceTimer) {
    clearTimeout(giftDebounceTimer);
  }
  giftDebounceTimer = setTimeout(() => {
    giftDebounceTimer = null;
    runSyncExclusiveGifts();
  }, GIFT_DEBOUNCE_MS);
}

function isExclusiveMainVariantId(variantId) {
  const config = getExclusiveGiftConfig();
  if (!config?.groups?.length || variantId == null) {
    return false;
  }
  const sid = String(variantId);
  for (let i = 0; i < config.groups.length; i++) {
    const ids = config.groups[i].mainVariantIds || [];
    for (let j = 0; j < ids.length; j++) {
      if (String(ids[j]) === sid) {
        return true;
      }
    }
  }
  return false;
}

function isExclusiveGiftPdp() {
  const config = getExclusiveGiftConfig();
  if (!config) {
    return false;
  }
  const handles = config.pdpProductHandles?.length
    ? config.pdpProductHandles
    : config.pdpProductHandle
      ? [config.pdpProductHandle]
      : [];
  for (let i = 0; i < handles.length; i++) {
    if (window.location.pathname.includes(`/products/${handles[i]}`)) {
      return true;
    }
  }
  return false;
}

// 全局导出
window.scheduleSyncExclusiveGifts = scheduleSyncExclusiveGifts;
window.runSyncExclusiveGifts = runSyncExclusiveGifts;
window.isExclusiveMainVariantId = isExclusiveMainVariantId;
window.isExclusiveGiftPdp = isExclusiveGiftPdp;
window.getExclusiveGiftConfig = getExclusiveGiftConfig;
window.getExclusivePropertyKey = getExclusivePropertyKey;
window.computeTargetGiftQuantities = computeTargetGiftQuantities;
window.buildGiftUpdates = buildGiftUpdates;
window.getExclusiveGiftOutOfStockIds = getExclusiveGiftOutOfStockIds;
window.getGiftDataOutOfStockIds = getGiftDataOutOfStockIds;
window.isLineFromPdp = isLineFromPdp;
window.isGiftSelectedOnLine = isGiftSelectedOnLine;
window.countEligibleMainQtyForGiftRule = countEligibleMainQtyForGiftRule;
window.eligibleParentLinesForGiftRule = eligibleParentLinesForGiftRule;
window.isClaimedGiftLine = isClaimedGiftLine;
window.qtyForVariantInCart = qtyForVariantInCart;
window.addExclusiveGiftOptOut = addExclusiveGiftOptOut;

// 向后兼容 —— 旧函数名作为别名
window.scheduleSyncOpendots2Gifts = scheduleSyncExclusiveGifts;
window.runSyncOpendots2Gifts = runSyncExclusiveGifts;
window.isOpendots2MainVariantId = isExclusiveMainVariantId;
window.isOpendots2Pdp = isExclusiveGiftPdp;
window.getOpendots2PropertyKey = getExclusivePropertyKey;
window.shouldAttachOpendots2GiftProperty = function () { return false; };
window.getOpendots2GiftPropertyForPdp = function () { return 'towel'; };
window.getLineGiftChoice = function () { return 'towel'; };
window.addOpendots2MainViaCart = function () {
  return Promise.resolve(null);
};
